/**
 * Help articles — boundary contract schemas (help-center-chatbot DC-2).
 *
 *   GET /landlord/api/help/articles?path=   — articles for a screen (summary fields only)
 *   GET /landlord/api/help/articles/:id     — one article, or the retired notice
 *   GET /landlord/api/help/categories       — categories with at least one current article
 *   GET /landlord/api/help/categories/:category/articles — one category's current articles (the topic view)
 *
 * Owned here: `apps/api/src/routes/manager/help.routes.ts` registers these schemas and the landlord
 * UI derives its types from the same objects (packages/api-types/README.md).
 */
import { z } from 'zod';
/** Article kinds (DC-1). */
export const helpArticleKindSchema = z.enum(['how-to', 'faq', 'concept', 'troubleshooting']);
export const ArticleSummarySchema = z.object({
    id: z.string(),
    title: z.string(),
    summary: z.string(),
    kind: helpArticleKindSchema,
    /** Controlled vocabulary owned by `@resida/help-content` (BR-14); the label is on `CategoryItem`. */
    category: z.string(),
});
const articleRefSchema = z.object({ id: z.string(), title: z.string() });
export const ArticleViewSchema = ArticleSummarySchema.extend({
    status: z.literal('current'),
    /** ISO calendar date of the last review (BR-8). */
    reviewedAt: z.string(),
    legalAdjacent: z.boolean(),
    /** Markdown whose every `help-asset:<id>` image is already an `<img>` with its CDN URL and manifest width and height (DC-8). */
    body: z.string(),
    related: z.array(articleRefSchema),
});
/** What a retired article's URL resolves to (BR-46). */
export const RetiredNoticeSchema = z.object({
    status: z.literal('retired'),
    id: z.string(),
    title: z.string(),
    replacedBy: articleRefSchema.nullable(),
});
export const ArticleResponseSchema = z.discriminatedUnion('status', [ArticleViewSchema, RetiredNoticeSchema]);
export const CategoryItemSchema = z.object({
    category: z.string(),
    label: z.string(),
    articleCount: z.number().int().min(1),
    /** First article of the category by `order`, then title. */
    topArticle: articleRefSchema,
});
export const CategoryParamsSchema = z.object({ category: z.string().min(1).max(100) });
/** A category's current articles by `order` then title: the topic view behind a topic card or panel row. */
export const CategoryArticlesSchema = z.object({
    category: z.string(),
    label: z.string(),
    articles: z.array(ArticleSummarySchema).min(1),
});
export const ArticleListQuerySchema = z.object({
    /** The landlord's current pathname; the server matches it against each article's `screens` patterns. */
    path: z.string().max(2048),
});
export const ArticleParamsSchema = z.object({ id: z.string().min(1).max(100) });
//# sourceMappingURL=article.js.map